import React from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { Button } from "shared/ui/buttons/Button"
import { ProductInput } from "shared/ui/product-input/ProductInput"

import { zodResolver } from "@hookform/resolvers/zod"
import { ProductSelectOption } from "entities/products/types/ProductSelectOption"
import I18n from "i18n"
import z from "zod"

import { GroupEntryInput } from "../model/useFollowUpConfigurationAdminPageViewModel"

interface GroupEntryFormData {
  targetProductName: string | null
}

const groupEntrySchema = z.object({
  targetProductName: z.string().min(1, I18n.t("live:Validation.required"))
})

interface FollowUpConfigurationGroupAddFormProps {
  productOptions: ProductSelectOption[]
  onAdd: (data: GroupEntryInput) => Promise<void>
  onClose: () => void
  isCreating?: boolean
}

export const FollowUpConfigurationGroupAddForm = React.memo(
  ({
    productOptions,
    onAdd,
    onClose,
    isCreating
  }: FollowUpConfigurationGroupAddFormProps) => {
    const { t } = useTranslation()

    const form = useForm<GroupEntryFormData>({
      resolver: zodResolver<
        GroupEntryFormData,
        unknown,
        z.infer<typeof groupEntrySchema>
      >(groupEntrySchema),
      defaultValues: { targetProductName: null }
    })

    const handleSubmit = useEvent(
      form.handleSubmit(async data => {
        if (!data.targetProductName) return

        await onAdd({ targetProductName: data.targetProductName })
        onClose()
      })
    )

    return (
      <FormProvider {...form}>
        <form
          className="follow-up-config-admin-page__group-add-form"
          onSubmit={handleSubmit}
        >
          <ProductInput
            name="targetProductName"
            label={t("FollowUpConfiguration.Fields.TargetProduct")}
            placeholder={t("FollowUpConfiguration.Placeholders.TargetProduct")}
            className="follow-up-config-admin-page__group-add-field"
            options={productOptions}
            multiple={false}
            required
            autoFocus
          />

          <div className="follow-up-config-admin-page__group-add-actions">
            <Button variant="primary" type="submit" disabled={isCreating}>
              {t("General.Add")}
            </Button>
            <Button variant="tertiary" onClick={onClose}>
              {t("General.Cancel")}
            </Button>
          </div>
        </form>
      </FormProvider>
    )
  }
)

FollowUpConfigurationGroupAddForm.displayName =
  "FollowUpConfigurationGroupAddForm"
